import { LIMIT_USAGE } from 'pricing-pages-lib/constants/EarlyRequesterNames';
import ProductInfoContainer from 'pricing-pages-lib/js/components/ProductInfoContainer.tsx';
import getUsageTracker from 'pricing-pages-lib/utils/getUsageTracker';
import { FreeToolsFAQs } from 'pricing-pages-ui-components/frequentlyAskedQuestions/FreeToolsFAQs';
import { PPUIPageHeader } from 'pricing-pages-ui-components/semantic/PageHeader';
import { PageSection } from 'pricing-pages-ui-components/semantic/PageSection';
import { useEffect } from 'react';
import { NavMarker } from 'react-rhumb';
import { CMS, COMMERCE, MARKETING, OPERATIONS, SALES, SERVICE } from 'self-service-api/constants/ProductLines';
import { SUITE_STARTER } from 'self-service-api/constants/UpgradeProducts';
import { buildFilteredHighlightedProducts } from 'ui-addon-upgrades/_core/features/FeaturesUtils';
import { getPrimaryTracker } from 'usage-tracker-container';
import BannerSection from '../components/banners/BannerSection';
import ComparePlansLink from '../components/common/ComparePlansLink';
import PhoneSupportMiddle from '../components/common/PhoneSupportMiddle';
import ProductPageSubtitle from '../components/common/ProductPageSubtitle';
import ProductPageTitle from '../components/common/ProductPageTitle';
import CodeSplitEnvWrapper from '../components/common/env/CodeSplitEnvWrapper';
import FallbackErrorBanner from '../components/common/loading/FallbackErrorBanner';
import ErrorBoundary from '../components/error-boundary/ErrorBoundary';
import { ERROR_SEVERITY } from '../components/error-boundary/error-severity';
import withErrorBoundary from '../components/error-boundary/withErrorBoundary';
import SuiteFeatureTable from '../components/feature-table/suite-feature-table/SuiteFeatureTable';
import HeaderLayout from '../components/layout/HeaderLayout';
import { FreePageCardContainer } from '../components/merch-card/card-containers/FreePageCardContainer';
import { FREE_TOOLS_PAGE, FREE_TOOLS_PAGE_CONTENT } from '../constants/ErrorBoundaryNames';
import { DEFAULT } from '../constants/ErrorCauses';
import { getMerchCardsToShow } from '../constants/merchCards/MerchCardsToShow';
import { NavItemEnum } from '../constants/routing/NavItems';
import { SCREENS } from '../constants/tracking/Screens';
import { useGlobalLoading } from '../hooks/loadingHooks';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useShouldHideOpsOrSuitePage } from '../hooks/useShouldHideOpsOrSuitePage';
import { useAppSelector } from '../reducers';
import { isUngatedForInbound25Selector } from '../selectors/GatingSelectors';
import { trackPageView } from '../utils/tracking/trackingEventUtils';
import { useQueryParamTerm, useQueryParamsEffect } from '../utils/urls/appQueryParamUtils';
import { useIsFreePortal } from '../v2/hooks/useProductInventory';
import ErrorPage from './ErrorPage';
import MissingInfoFallbackPage from './MissingInfoFallbackPage';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const FreeToolsPage = () => {
  useDocumentTitle('crm');
  const isAppReady = useGlobalLoading();
  const isUngatedForInbound = useAppSelector(isUngatedForInbound25Selector);
  const {
    queryParamTerm
  } = useQueryParamTerm();
  useQueryParamsEffect({
    term: queryParamTerm
  });
  const {
    isFreePortal,
    loading: isInventoryLoading
  } = useIsFreePortal();
  useEffect(() => {
    getUsageTracker().setProperties({
      defaultedTab: '',
      tab: ''
    });
    trackPageView({
      screen: SCREENS.FREE
    });
  }, []);
  const productLevels = getMerchCardsToShow({
    isFreeToStarter: true
  });
  const marketingHighlightedProducts = buildFilteredHighlightedProducts({
    productLine: MARKETING,
    productLevels
  });
  const salesHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SALES
  });
  const serviceHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: SERVICE
  });
  const operationsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: OPERATIONS
  });
  const cmsHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: CMS
  });
  const commerceHighlightedProducts = buildFilteredHighlightedProducts({
    productLevels,
    productLine: COMMERCE
  });
  const productsByProductLine = {
    [MARKETING]: marketingHighlightedProducts,
    [SALES]: salesHighlightedProducts,
    [SERVICE]: serviceHighlightedProducts,
    [OPERATIONS]: operationsHighlightedProducts,
    [CMS]: cmsHighlightedProducts,
    [COMMERCE]: commerceHighlightedProducts
  };
  const shouldHideOpsOrSuitePage = useShouldHideOpsOrSuitePage();
  const showFeatureTable = !shouldHideOpsOrSuitePage;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: /*#__PURE__*/_jsx(ErrorBoundary, {
      fallback: /*#__PURE__*/_jsx(MissingInfoFallbackPage, {
        navItem: NavItemEnum.FREE
      }),
      errorBoundaryName: FREE_TOOLS_PAGE_CONTENT,
      severity: ERROR_SEVERITY.HIGH,
      children: /*#__PURE__*/_jsxs("div", {
        "data-selenium-id": "page-success",
        children: [isAppReady && /*#__PURE__*/_jsx(NavMarker, {
          name: "FREE_TOOLS_PAGE"
        }), /*#__PURE__*/_jsx(HeaderLayout, {
          selectedNavItem: NavItemEnum.FREE,
          children: /*#__PURE__*/_jsxs(ProductInfoContainer, {
            children: [/*#__PURE__*/_jsx(BannerSection, {
              screen: NavItemEnum.FREE
            }), /*#__PURE__*/_jsx(FallbackErrorBanner, {}), /*#__PURE__*/_jsxs(PPUIPageHeader, {
              children: [/*#__PURE__*/_jsx(ProductPageTitle, {
                nonProductIdentifier: NavItemEnum.FREE
              }), /*#__PURE__*/_jsx(ProductPageSubtitle, {
                nonProductIdentifier: NavItemEnum.FREE
              })]
            }), /*#__PURE__*/_jsx(FreePageCardContainer, {}), showFeatureTable && /*#__PURE__*/_jsx(ComparePlansLink, {}), /*#__PURE__*/_jsx(CodeSplitEnvWrapper, {
              authedComponentLoader: () => import( /* webpackChunkName: "authed-chunk" */`pricing-pages-ui-components/limit-usage/LimitUsageSection`),
              authedComponentProps: {
                isFreePortal: isFreePortal && !isInventoryLoading,
                earlyRequestName: LIMIT_USAGE,
                usageTracker: getPrimaryTracker(),
                useCompactGrid: true
              }
            }), showFeatureTable && /*#__PURE__*/_jsx(PageSection, {
              children: /*#__PURE__*/_jsx(SuiteFeatureTable, {
                productsByProductLine: productsByProductLine,
                screen: SCREENS.FREE,
                isFreeToStarter: true
              })
            }), /*#__PURE__*/_jsx(PhoneSupportMiddle, {
              phoneSourceId: "crm-middle",
              screen: SCREENS.FREE,
              upgradeProduct: SUITE_STARTER
            }), /*#__PURE__*/_jsx(FreeToolsFAQs, {
              columns: isUngatedForInbound ? 2 : 1
            })]
          })
        })]
      })
    })
  });
};
export default withErrorBoundary(FreeToolsPage, {
  FallbackComponent: () => /*#__PURE__*/_jsx(ErrorPage, {
    errorCause: DEFAULT
  }),
  errorBoundaryName: FREE_TOOLS_PAGE,
  severity: ERROR_SEVERITY.HIGH
});